import React from "react";
import { useTranslation } from "react-i18next";
import { conceptsOf, listLevels } from "../lib/content";
import { useProgress } from "../lib/progress";
import { hrefFor } from "../lib/router";
import { BossBadge, DifficultyBadge, ProgressBar, TypeBadge } from "./Badges";
import { CheckIcon, ChevronRightIcon } from "./Icons";
import { AchievementsPanel, ForgePanel, SkillsPanel } from "./Rpg";

function TrackPage({ track }) {
  const { t } = useTranslation();
  const { solved, lastLevel } = useProgress();

  const entries = listLevels(track);
  const levels = entries.map((entry) => entry.level);
  const solvedCount = levels.filter((level) => solved[level.id]).length;
  const firstUnsolved = levels.find((level) => !solved[level.id]);
  const resume = levels.find((level) => level.id === lastLevel[track.id]) || firstUnsolved || levels[0];
  const hasStarted = solvedCount > 0 || Boolean(lastLevel[track.id]);

  const concepts = conceptsOf(levels);

  return (
    <main id="main" className="page track-page">
      <section className="track-hero">
        <div className="track-hero__text">
          <p className="eyebrow">
            {t("track.chapters", { count: track.chapters.length })} · {t("track.levels", { count: levels.length })}
          </p>
          <h1>{track.title}</h1>
          <p className="track-hero__tagline">{track.tagline}</p>
          <p className="track-hero__description">{track.description}</p>
          <div className="track-hero__progress">
            <ProgressBar value={solvedCount} total={levels.length} label={t("track.progress", { solved: solvedCount, total: levels.length })} />
            <span className="muted small">{t("track.progress", { solved: solvedCount, total: levels.length })}</span>
          </div>
          <div className="track-hero__actions">
            <a className="button button--primary" href={hrefFor(track.id, resume.id)}>
              {hasStarted ? t("track.continue", { title: resume.title }) : t("track.start")}
              <ChevronRightIcon />
            </a>
            {hasStarted && (
              <a className="button button--ghost" href={hrefFor(track.id, levels[0].id)}>
                {t("track.review")}
              </a>
            )}
          </div>
        </div>
        <ForgePanel track={track} />
      </section>

      <div className="track-layout">
        <ol className="chapters">
          {track.chapters.map((chapter, chapterIndex) => {
            const chapterSolved = chapter.levels.filter((level) => solved[level.id]).length;
            return (
              <li key={chapter.id} className="chapter">
                <header className="chapter__header">
                  <div>
                    <p className="eyebrow">{t("track.chapter", { number: chapterIndex + 1 })}</p>
                    <h2>{chapter.title}</h2>
                    <p className="muted">{chapter.summary}</p>
                  </div>
                  <div className="chapter__progress">
                    <span>
                      {chapterSolved}/{chapter.levels.length}
                    </span>
                    <ProgressBar value={chapterSolved} total={chapter.levels.length} label={chapter.title} />
                  </div>
                </header>
                <ul className="level-list">
                  {chapter.levels.map((level) => {
                    const isSolved = Boolean(solved[level.id]);
                    return (
                      <li key={level.id}>
                        <a
                          className={`level-row ${isSolved ? "is-solved" : ""} ${level.boss ? "level-row--boss" : ""}`}
                          href={hrefFor(track.id, level.id)}
                        >
                          <span className="level-row__status" aria-label={isSolved ? t("level.solved") : undefined}>
                            {isSolved && <CheckIcon size={12} strokeWidth={3} />}
                          </span>
                          <span className="level-row__title">{level.title}</span>
                          <span className="level-row__meta">
                            {level.boss && <BossBadge />}
                            <TypeBadge type={level.type} />
                            <DifficultyBadge difficulty={level.difficulty} />
                          </span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ol>

        <aside className="track-side">
          <AchievementsPanel track={track} />
          <SkillsPanel track={track} concepts={concepts} />
        </aside>
      </div>
    </main>
  );
}

export default TrackPage;
